/**
 * Grundlayout der Agent-Oberflaeche: Kopfzeile mit Mandantenwahl und
 * Navigation (Spec 7).
 *
 * `adminOnly`-Punkte blendet das UI fuer Agents und Viewer aus. Das ist reine
 * Bequemlichkeit — die API lehnt sie ohnehin mit 403 ab.
 */
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router';
import { AccountMenu } from '@/components/AccountMenu';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { to: '/', labelKey: 'nav.dashboard', end: true, adminOnly: false, aiOnly: false, platformOnly: false },
  { to: '/tickets', labelKey: 'nav.tickets', end: false, adminOnly: false, aiOnly: false, platformOnly: false },
  { to: '/contacts', labelKey: 'nav.contacts', end: false, adminOnly: false, aiOnly: false, platformOnly: false },
  { to: '/snippets', labelKey: 'nav.snippets', end: false, adminOnly: false, aiOnly: false, platformOnly: false },
  // Nur sichtbar, wenn der Mandant die KI eingeschaltet hat (Spec 3.2).
  { to: '/ai', labelKey: 'nav.ai', end: false, adminOnly: false, aiOnly: true, platformOnly: false },
  { to: '/queues', labelKey: 'nav.queues', end: false, adminOnly: true, aiOnly: false, platformOnly: false },
  { to: '/mail', labelKey: 'nav.mail', end: false, adminOnly: true, aiOnly: false, platformOnly: false },
  { to: '/teams', labelKey: 'nav.teams', end: false, adminOnly: true, aiOnly: false, platformOnly: false },
  { to: '/members', labelKey: 'nav.members', end: false, adminOnly: true, aiOnly: false, platformOnly: false },
  { to: '/integrations', labelKey: 'nav.integrations', end: false, adminOnly: true, aiOnly: false, platformOnly: false },
  { to: '/audit-log', labelKey: 'nav.audit_log', end: false, adminOnly: true, aiOnly: false, platformOnly: false },
  // Betreiber-Ebene: kein Mandanten-Admin sieht das, auch nicht der eigene.
  // Ausblenden ist Bequemlichkeit — die API prueft `is_platform_admin`.
  // „Mandanten" steht vor „Systemmail", weil es der erste Schritt beim
  // Einrichten ist (Mandant anlegen → Queue → Quelle → Mitglieder).
  { to: '/platform/tenants', labelKey: 'nav.tenants', end: false, adminOnly: false, aiOnly: false, platformOnly: true },
  { to: '/platform/system-mail', labelKey: 'nav.platform_mail', end: false, adminOnly: false, aiOnly: false, platformOnly: true },
  // Der Verbindungs-Vault (ADR 0017): hier traegt der Betreiber jeden Zugang
  // genau einmal ein, alles Uebrige zeigt nur noch darauf.
  { to: '/platform/connections', labelKey: 'nav.connections', end: false, adminOnly: false, aiOnly: false, platformOnly: true },
  // „Sicherheit" steht nicht mehr hier, sondern als Reiter unter „Mein Konto"
  // im Avatar-Menue — es gehoert der Person, nicht dem Mandanten.
] as const;

export function AppLayout() {
  const { t } = useTranslation();
  const { user, activeMembership, setActiveTenant } = useSession();

  return (
    <div className="min-h-screen">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-6 py-3">
          <div className="mr-auto">
            <p className="font-semibold">{t('app.name')}</p>
            <p className="text-xs text-muted-foreground">{t('app.tagline')}</p>
          </div>

          {user && user.memberships.length > 1 ? (
            <label className="flex items-center gap-2 text-sm">
              <span className="sr-only">{t('tenant.switch')}</span>
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                value={activeMembership?.tenant_id ?? ''}
                onChange={(event) => setActiveTenant(event.target.value)}
              >
                {user.memberships.map((membership) => (
                  <option key={membership.tenant_id} value={membership.tenant_id}>
                    {membership.tenant_name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <LanguageSwitcher />

          <AccountMenu />
        </div>

        <nav className="mx-auto max-w-6xl px-6">
          <ul className="flex flex-wrap gap-1">
            {NAV_ITEMS.filter(
              (item) =>
                (!item.adminOnly ||
                  activeMembership?.role === 'admin' ||
                  user?.is_platform_admin) &&
                (!item.aiOnly || activeMembership?.ai_enabled === true) &&
                (!item.platformOnly || user?.is_platform_admin === true),
            ).map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'inline-block border-b-2 px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'border-primary font-medium text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
